import React from 'react';
import { Box, Card, Typography, useMediaQuery, useTheme, IconButton } from '@material-ui/core';
import { FieldArray, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { sortBy, isNumber } from 'lodash/fp';
import {
    DeductionReason, StockCardsQuery, UnitType,
} from '../../../../generated/graphql';
import FormWrapper from '../../../../components/forms/FormWrapper';
import { WarehouseCardCounterField } from '../../../../ui-kit';
import { sanitizeFormValues } from '../../../../utils';
import { SelectField } from '../../../../components/forms/fields';
import { ArrowBack } from '@material-ui/icons';

export interface DeductionItem {
    stockCardId: string;
    quantity: number;
    unitType: UnitType;
    alternativeUnit: UnitType;
    name: string;
    categoryName: string;
}

export interface WriteOffFormValues {
    deductionItems?: Array<DeductionItem>;
    reason: DeductionReason;
}

const QUANTITY_FIELD_NAME = 'quantity';
const UNIT_TYPE_FIELD_NAME = 'unitType';

interface WarehouseCardWriteOffFormProps {
    stockCards: StockCardsQuery['stockCards'];
    onClose: () => void;
    onSubmit: (values: WriteOffFormValues) => Promise<void>;
}

const WarehouseCardWriteOffForm = ({ stockCards, onSubmit, onClose }: WarehouseCardWriteOffFormProps) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const { t } = useTranslation();
    const dialogName = t('Write-off');
    const submitButtonText: string = t('Write-off items');

    const sortedStockCards = sortBy(['stockCard.stockCardCategory.name', 'stockCard.name'], stockCards);

    const deductionItems = sortedStockCards.map(({
        id,
        name,
        storageUnit,
        conversionUnit,
        stockCardCategory,
    }) => ({
        id,
        name,
        stockCardId: id,
        quantity: 0,
        unitType: storageUnit,
        alternativeUnit: conversionUnit,
        categoryName: stockCardCategory?.name,
    }) as DeductionItem);

    const initialValues = {
        deductionItems,
        reason: DeductionReason.Waste,
    };

    const onFormSubmit = async (values: WriteOffFormValues) => {
        const sanitizedValues = sanitizeFormValues(values);

        sanitizedValues.deductionItems = sanitizedValues.deductionItems?.reduce((acc: Array<DeductionItem>, deductionItem) => {
            if (isNumber(deductionItem.quantity) && deductionItem.quantity > 0) {
                acc.push(deductionItem);
            }

            return acc;
        }, []);

        await onSubmit(sanitizedValues);
    };

    const reasonOptions = [
        { id: DeductionReason.Waste, name: t(DeductionReason.Waste) },
        { id: DeductionReason.Writeoff, name: t(DeductionReason.Writeoff) },
    ];

    return (
        <Card style={{ height: '100%', maxWidth: isDesktop ? '35vw' : '100%', overflow: 'auto' }}>
            <Box px={3} py={2}>
                <Typography variant='h6' component='h1'>{dialogName}</Typography>
                {!isDesktop && (
                    <>
                        <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                            <ArrowBack />
                        </IconButton>
                        <Box mb={2} />
                    </>
                )}
                <Formik
                    initialValues={initialValues}
                    onSubmit={onFormSubmit}
                >
                    {(formikProps: FormikProps<WriteOffFormValues>) => {
                        return (
                            <FormWrapper isSubmitting={formikProps.isSubmitting} isDialogView submitText={submitButtonText}>
                                <SelectField
                                    helpertext={formikProps.values.reason === DeductionReason.Waste
                                        ? t('Waste will be calculated in total waste amount in your statistics')
                                        : undefined}
                                    variant='outlined'
                                    options={reasonOptions}
                                    name='reason'
                                    label={t('Reason')}
                                    formikProps={formikProps}
                                />
                                <Box mb={2} />
                                <FieldArray name="deductionItems">
                                    {() => formikProps.values.deductionItems?.map((deductionItem, i) => {
                                        return (
                                            <>
                                                <WarehouseCardCounterField<WriteOffFormValues>
                                                    isDanger
                                                    key={`deductionItems.${i}.name`}
                                                    categoryName={deductionItem.categoryName ?? t('Unassigned')}
                                                    label={deductionItem.name}
                                                    formikProps={formikProps}
                                                    inputProps={{
                                                    name: `deductionItems.${i}.${QUANTITY_FIELD_NAME}`,
                                                    placeholder: '',
                                                    initialValue: deductionItem.quantity,
                                                }}
                                                    selectProps={{
                                                    name: `deductionItems.${i}.${UNIT_TYPE_FIELD_NAME}`,
                                                    defaultValue: deductionItem.unitType,
                                                }}
                                                    storageUnit={deductionItem.unitType}
                                                    alternativeUnit={deductionItem.alternativeUnit as UnitType}
                                                />
                                                <Box mb={2} />
                                            </>
                                        );
                                    })}
                                </FieldArray>
                            </FormWrapper>
                        );
                    }}
                </Formik>
            </Box>
        </Card>
    );
};

export default WarehouseCardWriteOffForm;
